import React from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  HelpCircle,
  ChevronRight,
} from 'lucide-react';
import Navbar from '../common/Navbar';
import Footer from '../common/Footer';
import SEO from '../common/SEO';
import { getFaqBySlug, faqs } from '../../lib/faqData';
import '../../styles/Home.css';
import '../../styles/FaqPage.css';

export default function FaqDetailPage() {
  const { slug } = useParams();
  const faq = getFaqBySlug(slug);

  // FAQ Not Found Handler
  if (!faq) {
    return (
      <div className="sx-faq-root">
        <SEO
          title="FAQ Not Found | SenritsuX"
          description="The requested FAQ question could not be found. Browse all frequently asked questions about SenritsuX music distribution and artist services."
          noindex={true}
        />
        <Navbar />
        <main
          className="sx-faq-main"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
          }}
        >
          <div className="sx-faq-empty-card" style={{ maxWidth: '540px' }}>
            <HelpCircle size={48} className="sx-faq-empty-icon" aria-hidden="true" />
            <h1 className="sx-faq-empty-title">
              Question Not Found
            </h1>
            <p className="sx-faq-empty-desc">
              This FAQ question does not exist or may have been moved. Browse the complete knowledge base to find answers.
            </p>
            <Link
              to="/faq"
              className="sx-faq-empty-btn"
            >
              <ArrowLeft size={16} /> Browse All FAQs
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // SEO metadata & Structured Data
  const seoTitle = `${faq.question} | FAQ | SenritsuX`;
  const seoDescription = faq.answer.slice(0, 160).trim();

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      },
    ],
  };

  // Related questions (same category, excluding current)
  const related = faqs
    .filter((f) => f.category === faq.category && f.slug !== faq.slug)
    .slice(0, 3);

  return (
    <div className="sx-faq-root">
      <SEO
        title={seoTitle}
        description={seoDescription}
        canonical={`/faq/${faq.slug}`}
        jsonLd={jsonLd}
      />
      <Navbar />

      <main className="sx-faq-main">
        <div className="sx-faq-detail-container">

          {/* Breadcrumb Navigation */}
          <nav aria-label="Breadcrumb">
            <ol className="sx-faq-breadcrumb">
              <li>
                <Link to="/faq" className="sx-faq-breadcrumb-link">
                  <ArrowLeft size={13} aria-hidden="true" /> FAQ
                </Link>
              </li>
              <li aria-hidden="true">
                <ChevronRight size={14} color="rgba(255,255,255,0.3)" />
              </li>
              <li className="sx-faq-breadcrumb-current" aria-current="page">
                {faq.question}
              </li>
            </ol>
          </nav>

          {/* Header & Category Badge */}
          <header className="sx-faq-detail-header">
            <span className="sx-faq-detail-badge">
              {faq.category}
            </span>
            <h1 className="sx-faq-detail-title">
              {faq.question}
            </h1>
          </header>

          {/* Answer Card */}
          <article aria-label="Answer" className="sx-faq-detail-card">
            <p className="sx-faq-detail-answer">
              {faq.answer}
            </p>
          </article>

          {/* Related Questions */}
          {related.length > 0 && (
            <section aria-label="Related questions" className="sx-faq-related-section">
              <h2 className="sx-faq-related-heading">
                Related Questions
              </h2>
              <ul className="sx-faq-related-list">
                {related.map((r) => (
                  <li key={r.slug}>
                    <Link
                      to={`/faq/${r.slug}`}
                      className="sx-faq-related-card"
                    >
                      <span>{r.question}</span>
                      <ArrowRight size={14} color="#d9fb06" style={{ flexShrink: 0 }} aria-hidden="true" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Actions: Back to FAQ and Contact Support */}
          <div className="sx-faq-actions-row">
            <Link
              to="/faq"
              className="sx-faq-btn-secondary"
            >
              <ArrowLeft size={16} aria-hidden="true" /> Back to All FAQs
            </Link>
            <Link
              to="/contact"
              className="sx-faq-contact-link"
            >
              Still have questions? Contact Support <ArrowRight size={14} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
